import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardHeading,
  CardTitle
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PII_MASK_CLASS } from "@/lib/analytics";
import type { HttpTypes } from "@medusajs/types";
import { MapPin, Pencil, Trash2, Truck, Receipt } from "lucide-react";
import { memo } from "react";

interface AddressCardProps {
  /** The address data to display */
  address: HttpTypes.StoreCustomerAddress;
  /** Callback when edit button is clicked */
  onEdit: (address: HttpTypes.StoreCustomerAddress) => void;
  /** Callback when delete button is clicked */
  onDelete: (addressId: string) => void;
  /** Callback when set as default shipping is clicked */
  onSetDefaultShipping?: (addressId: string) => void;
  /** Callback when set as default billing is clicked */
  onSetDefaultBilling?: (addressId: string) => void;
  /** Whether this address is the default for shipping */
  isDefaultShipping?: boolean;
  /** Whether this address is the default for billing */
  isDefaultBilling?: boolean;
  /** Whether actions are disabled (e.g., during loading) */
  disabled?: boolean;
}

/**
 * Displays a single customer address with edit and delete actions
 *
 * Features:
 * - Accessible card structure with proper ARIA labels
 * - Keyboard navigable edit/delete buttons
 * - Responsive layout
 * - Visual distinction for address name
 * - Default shipping and billing badges
 * - Set as default shipping/billing actions
 */
export const AddressCard = memo(function AddressCard({
  address,
  onEdit,
  onDelete,
  onSetDefaultShipping,
  onSetDefaultBilling,
  isDefaultShipping = false,
  isDefaultBilling = false,
  disabled = false
}: AddressCardProps) {
  const fullName = [address.first_name, address.last_name]
    .filter(Boolean)
    .join(" ");

  const addressLine = [
    address.address_1,
    address.address_2,
    address.postal_code,
    address.city,
    address.province,
    address.country_code?.toUpperCase()
  ]
    .filter(Boolean)
    .join(", ");

  const hasAnyDefault = isDefaultShipping || isDefaultBilling;

  // Build aria label
  const defaultLabels = [];
  if (isDefaultShipping) defaultLabels.push("Default Shipping");
  if (isDefaultBilling) defaultLabels.push("Default Billing");
  const defaultAriaLabel =
    defaultLabels.length > 0 ? ` (${defaultLabels.join(", ")})` : "";

  return (
    // PII_MASK_CLASS: a delivery address is the salon's own business detail and
    // is masked out of session replays (ADR-0006). On the card root so every
    // line inside it is covered, not just the ones that exist today.
    <Card
      className={`relative ${PII_MASK_CLASS} ${hasAnyDefault ? "ring-2 ring-purple-500/70" : ""}`}
      role="article"
      aria-label={`Address: ${address.address_name || "Unnamed address"}${defaultAriaLabel}`}
    >
      <CardHeader className="flex-col items-stretch gap-2 py-4">
        <CardHeading className="flex items-center justify-between gap-3 space-y-0">
          <div className="flex min-w-0 items-center gap-2">
            <MapPin
              className="h-5 w-5 flex-shrink-0 text-purple-600 dark:text-purple-400"
              aria-hidden="true"
            />
            <CardTitle className="truncate text-base">
              {address.address_name || "Address"}
            </CardTitle>
          </div>
          <div
            className="flex flex-shrink-0 gap-1"
            role="group"
            aria-label="Address actions"
          >
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(address)}
              disabled={disabled}
              aria-label={`Edit ${address.address_name || "address"}`}
              className="h-8 w-8 p-0"
            >
              <Pencil className="h-4 w-4" aria-hidden="true" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(address.id)}
              disabled={disabled}
              aria-label={`Delete ${address.address_name || "address"}`}
              className="h-8 w-8 p-0 text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-950 dark:hover:text-red-300"
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </CardHeading>
        {(hasAnyDefault || address.company) && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 pl-7">
            {isDefaultShipping && (
              <Badge
                variant="secondary"
                className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
              >
                <Truck className="mr-1 h-3 w-3" aria-hidden="true" />
                Default Shipping
              </Badge>
            )}
            {isDefaultBilling && (
              <Badge
                variant="secondary"
                className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
              >
                <Receipt className="mr-1 h-3 w-3" aria-hidden="true" />
                Default Billing
              </Badge>
            )}
            {address.company && (
              <CardDescription className="min-w-0 truncate">
                {address.company}
              </CardDescription>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <div className="space-y-1">
          <p className="font-medium text-gray-900 dark:text-white">
            {fullName}
          </p>
          <p className="text-gray-600 dark:text-gray-400">{addressLine}</p>
          {address.phone && (
            <p className="text-gray-600 dark:text-gray-400">{address.phone}</p>
          )}
        </div>

        {/* Set as default buttons */}
        {(!isDefaultShipping || !isDefaultBilling) && (
          <div className="border-border flex flex-wrap gap-x-4 gap-y-1 border-t pt-3">
            {!isDefaultShipping && onSetDefaultShipping && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onSetDefaultShipping(address.id)}
                disabled={disabled}
                className="h-auto px-0 py-1 text-sm text-blue-600 hover:bg-transparent hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              >
                <Truck className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                Set as default shipping
              </Button>
            )}
            {!isDefaultBilling && onSetDefaultBilling && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onSetDefaultBilling(address.id)}
                disabled={disabled}
                className="h-auto px-0 py-1 text-sm text-green-600 hover:bg-transparent hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
              >
                <Receipt className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                Set as default billing
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
});
